// semeia-disable-reexport
import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import type { CardReaderAdapter } from "parog-web/cnam-adapters/card-reader-adapter";
import { practiceLocationIndex } from "parog-web/cnam-adapters/utils";
import type { CPSInformations, PracticeLocation } from "parog-web/cnam/types";
import CnamServicesAccessFormAuthenticationModifyPracticeLocationComponent from "parog-web/components/cnam-services-access-form/authentication/modify-practice-location";
import CnamServicesAccessFormAuthenticationSetPracticeLocationComponent from "parog-web/components/cnam-services-access-form/authentication/set-practice-location";
import type MedicModel from "parog-web/models/medic";
import type FetcherService from "parog-web/services/fetcher";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";
import CnamContext, { State } from "./abstract/cnam-context";

interface AdministrativeCPSInformations {
  billingNumber?: string;
  finess?: string;
  siret?: string;
}

export abstract class AuthenticationContext extends CnamContext {
  @tracked availableLocations?: PracticeLocation[] = [];
  @tracked cpsInformations?: CPSInformations;
  @tracked cpsPracticeLocationIndice?: number;
  @tracked hasCheckedRates = false;
  @tracked practiceLocation?: string;
  @tracked shouldUpdatePracticeLocation?: boolean;

  cardReaderAdapter: CardReaderAdapter;
  fetcher: FetcherService;
  declare forcedIdentifier: string;
  medic: MedicModel;

  get billingNumber(): string | undefined {
    // If the finess is forced, we use it
    const forcedIdentifier = this.medic.forceIdentifierForInsRetrieval;

    const administrativeInfosFromCurrentPracticeLocation =
      this.retrieveAdministrativeInformationFromPraticeLocation(
        this.currentPracticeLocation
      );
    // If the PS is a liberal, it has a billing number
    const billingNumber =
      administrativeInfosFromCurrentPracticeLocation.billingNumber;
    // Otherwise, it should be a employee. Then, the FINESS or the SIRET of the structure should be stored under structureId.
    const finess = administrativeInfosFromCurrentPracticeLocation.finess;
    const siret = administrativeInfosFromCurrentPracticeLocation.siret;

    return forcedIdentifier || billingNumber || finess || siret;
  }

  get currentPracticeLocation(): PracticeLocation | undefined {
    return this.cpsPracticeLocationIndice != undefined &&
      this.cpsPracticeLocationIndice >= 0
      ? this.availableLocations?.[this.cpsPracticeLocationIndice]
      : undefined;
  }

  abstract get isAuthenticated(): boolean;

  get practiceLocationsWithValidBillingNumber(): PracticeLocation[] {
    return (
      this.availableLocations?.filter(practiceLocation => {
        const administrativeInfos =
          this.retrieveAdministrativeInformationFromPraticeLocation(
            practiceLocation
          );
        return (
          administrativeInfos.billingNumber ||
          administrativeInfos.finess ||
          administrativeInfos.siret
        );
      }) || []
    );
  }

  constructor(
    cnamAdapterName: string,
    cardReaderAdapter: CardReaderAdapter,
    medic: MedicModel,
    store: Store,
    fetcher: FetcherService,
    requestManager
  ) {
    super(store, requestManager, cnamAdapterName);
    this.cardReaderAdapter = cardReaderAdapter;
    this.medic = medic;
    this.store = store;
    this.fetcher = fetcher;

    this.states = [
      new State(
        "set-practice-location",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.shouldUpdatePracticeLocation ||
              this._shouldUpdatePracticeLocation() ||
              !(
                this.cpsPracticeLocationIndice != undefined &&
                this.cpsPracticeLocationIndice >= 0 &&
                Boolean(this.practiceLocation)
              )
          );
        },
        async (): Promise<void> => {
          await this.updatePracticeLocationNameAndIndice();

          this.shouldUpdatePracticeLocation = false;
        },
        CnamServicesAccessFormAuthenticationSetPracticeLocationComponent
      ),
      new State(
        "allow-practice-location-modifications",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.shouldUpdatePracticeLocation == undefined &&
              Boolean(this.practiceLocation)
          );
        },
        async (): Promise<void> => {
          return;
        },
        CnamServicesAccessFormAuthenticationModifyPracticeLocationComponent
      ),
      new State(
        "check-rates",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.hasCheckedRates && Boolean(this.cpsInformations)
          );
        },
        async (): Promise<void> => {
          const twoDaysBefore = new Date(
            new Date().setDate(new Date().getDate() - 2)
          );
          twoDaysBefore.setHours(0, 0, 0, 0);
          await this.fetcher.loadRecords(
            "dmp-retrieval-started-journal-event",
            {
              filter: {
                cps_id: this.cpsInformations?.medicInternalId,
                occurred_at_gte: formatToISO8601Date(twoDaysBefore)
              }
            }
          );
          this.hasCheckedRates = true;
        }
      )
    ];
  }

  _availableLocationsKey(): string {
    return "authentication:available-locations";
  }

  _cpsPracticeLocationIndiceKey(): string {
    return "authentication:cps-practice-location-indice";
  }

  _getPracticeLocationIndex = (): number => {
    const availableLocations =
      this.cpsInformations?.practiceLocations || this.availableLocations;
    if (availableLocations) {
      return practiceLocationIndex(this.practiceLocation, availableLocations);
    }
    return -1;
  };

  _practiceLocationKey(): string {
    return "authentication:practice-location";
  }

  _shouldUpdatePracticeLocation = (): boolean => {
    return (
      !this.practiceLocation ||
      this.shouldUpdatePracticeLocation ||
      this._getPracticeLocationIndex() < 0 // if the medic practice location is not on the CPS card any more
    );
  };

  _updateMedicWithNewPracticeLocationIfNeeded = async (): Promise<void> => {
    if (this.medic.practiceLocation !== this.practiceLocation) {
      this.medic.practiceLocation = this.practiceLocation;
      await this.medic.save();
    }
  };

  override reset(): void {
    super.reset();
    this.cpsPracticeLocationIndice = Number(
      getItemFromLocalStorage(this._cpsPracticeLocationIndiceKey())
    );
    const practiceLocationFromLocalStorage = getItemFromLocalStorage(
      this._practiceLocationKey()
    );
    this.practiceLocation =
      practiceLocationFromLocalStorage == null
        ? undefined
        : practiceLocationFromLocalStorage;
    this.availableLocations = getItemFromLocalStorage(
      this._availableLocationsKey()
    )
      ? JSON.parse(
          getItemFromLocalStorage(this._availableLocationsKey()) as string
        )
      : undefined;

    this.shouldUpdatePracticeLocation = undefined;
    this.cpsInformations = undefined;
    this.hasCheckedRates = false;
  }

  retrieveAdministrativeInformationFromPraticeLocation(
    practiceLocation: PracticeLocation | undefined
  ): AdministrativeCPSInformations {
    return {
      billingNumber: practiceLocation?.billingNumber,
      // If the structureId is prefixed with a "1", it's a FINESS
      finess: practiceLocation?.structureId?.startsWith("1")
        ? practiceLocation.structureId
        : undefined,
      // If the structureId is prefixed with a "4", it's a SIRET
      siret: practiceLocation?.structureId?.startsWith("4")
        ? practiceLocation.structureId
        : undefined
    };
  }

  setCpsPracticeLocationIndice(cpsPracticeLocationIndice: number) {
    setItemInLocalStorage(
      this._cpsPracticeLocationIndiceKey(),
      cpsPracticeLocationIndice.toString()
    );
  }

  setPracticeLocationInLocalStorage(practiceLocation?: string): void {
    setItemInLocalStorage(
      this._practiceLocationKey(),
      practiceLocation as string
    );
  }

  async updatePracticeLocationNameAndIndice() {
    this.setPracticeLocationInLocalStorage(this.practiceLocation);
    await this._updateMedicWithNewPracticeLocationIfNeeded();

    this.cpsPracticeLocationIndice = this._getPracticeLocationIndex();
    this.setCpsPracticeLocationIndice(this.cpsPracticeLocationIndice);
  }
}
